import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import getApiBaseUrl from '../utils/api';

const CheckoutQR = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [isPolling, setIsPolling] = useState(true);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds

  const { qrCode, orderId, paymentId } = location.state || {};

  // Redirect if no QR data
  useEffect(() => {
    if (!qrCode || !orderId || !paymentId) {
      navigate('/checkout/payment');
    }
  }, [qrCode, orderId, paymentId, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0 && paymentStatus === 'pending') {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setPaymentStatus('expired');
      setIsPolling(false);
    }
  }, [timeLeft, paymentStatus]);

  // Poll payment status
  useEffect(() => {
    if (!isPolling || paymentStatus !== 'pending') return;

    const pollStatus = async () => {
      try {
        const API_BASE_URL = getApiBaseUrl();
          
        const response = await fetch(`${API_BASE_URL}/xendit/payment-status?paymentId=${paymentId}`);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'SUCCEEDED') {
            setPaymentStatus('succeeded');
            setIsPolling(false);
            setTimeout(() => {
              navigate(`/checkout/success?order_id=${orderId}&payment_id=${paymentId}`, { 
                state: { 
                  orderId, 
                  paymentId,
                  paymentMethod: 'QR Code' 
                }
              });
            }, 2000);
          } else if (data.status === 'FAILED' || data.status === 'EXPIRED') {
            setPaymentStatus('failed');
            setIsPolling(false);
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    const interval = setInterval(pollStatus, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [isPolling, paymentStatus, paymentId, orderId, navigate]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle manual refresh
  const handleRefreshStatus = async () => {
    try {
      const API_BASE_URL = getApiBaseUrl();
        
      const response = await fetch(`${API_BASE_URL}/xendit/payment-status?paymentId=${paymentId}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'SUCCEEDED') {
          setPaymentStatus('succeeded');
          navigate(`/checkout/success?order_id=${orderId}&payment_id=${paymentId}`, { 
            state: { 
              orderId, 
              paymentId,
              paymentMethod: 'QR Code' 
            }
          });
        } else if (data.status === 'FAILED' || data.status === 'EXPIRED') {
          setPaymentStatus('failed');
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  if (!qrCode) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">QR Code Not Available</h1>
          <p className="text-light mb-8">Please try again from the payment page.</p>
          <Button onClick={() => navigate('/checkout/payment')} variant="primary">
            Back to Payment
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">
            Scan QR Code to Pay
          </h1>

          {paymentStatus === 'pending' && (
            <div className="mb-6">
              <div className="bg-blue-900/20 text-blue-400 p-4 rounded-lg mb-4">
                <p className="font-semibold mb-2">Payment Pending</p>
                <p className="text-sm">Scan the QR code below with your mobile payment app</p>
              </div>
              <div className="text-lg font-mono text-primary mb-2">
                Time remaining: {formatTime(timeLeft)}
              </div>
            </div>
          )}

          {paymentStatus === 'succeeded' && (
            <div className="bg-green-900/20 text-green-400 p-4 rounded-lg mb-6">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-semibold mb-2">Payment Successful!</p>
              <p className="text-sm">Redirecting to confirmation page...</p>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div className="bg-red-900/20 text-red-400 p-4 rounded-lg mb-6">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-semibold mb-2">Payment Failed</p>
              <p className="text-sm">The payment could not be completed</p>
            </div>
          )}

          {paymentStatus === 'expired' && (
            <div className="bg-yellow-900/20 text-yellow-400 p-4 rounded-lg mb-6">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-semibold mb-2">QR Code Expired</p>
              <p className="text-sm">Please generate a new QR code to continue</p>
            </div>
          )}

          {/* QR Code Display */}
          <div className="bg-white p-8 rounded-lg inline-block mb-6">
            <img 
              src={qrCode} 
              alt="Payment QR Code" 
              className="w-64 h-64 mx-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="w-64 h-64 bg-gray-200 flex items-center justify-center text-gray-500 text-sm hidden">
              QR Code not available
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-6 text-left">
            <h3 className="font-bold text-light mb-4">How to Pay:</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-400">
              <li>Open your mobile payment app (e.g., mobile banking, e-wallet)</li>
              <li>Look for "Scan QR" or "QR Pay" option</li>
              <li>Point your camera at the QR code above</li>
              <li>Confirm the payment amount and complete the transaction</li>
              <li>Wait for payment confirmation (this page will update automatically)</li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4">
            {paymentStatus === 'pending' && (
              <>
                <Button 
                  variant="secondary" 
                  onClick={handleRefreshStatus}
                >
                  Check Payment Status
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/checkout/payment')}
                >
                  Try Different Method
                </Button>
              </>
            )}

            {(paymentStatus === 'failed' || paymentStatus === 'expired') && (
              <>
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/checkout/payment')}
                >
                  Try Again
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => navigate('/products')}
                >
                  Back to Products
                </Button>
              </>
            )}
          </div>

          {/* Order Information */}
          <div className="mt-8 pt-8 border-t border-gray-800 text-left">
            <h3 className="font-bold text-light mb-2">Order Information:</h3>
            <p className="text-gray-400 text-sm">Order ID: {orderId}</p>
            <p className="text-gray-400 text-sm">Payment ID: {paymentId}</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutQR;
